import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { WithChildrenCommonProps } from '@types';
import { useToggle, PopoutPlacement } from '@hooks';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { DropdownOptionList, IDropdownOption } from '@components/dropdown-option-list';
import { FieldContainer, FieldContent, FieldLabel, FieldSideContent } from '@components/form/field';
import { Icon, IconNames } from '@components/icon';
import { Typography } from '@components/typography';
import { getTypographySelectStylesAsClassName } from './select-styles';
import { getFieldChevronStylesAsClassName } from '../field/field-styles';
export type ISelectOption = IDropdownOption<unknown>;
export type ISelect = WithChildrenCommonProps<{
  /* Information displayed when hovering clear button */
  clearButtonTooltip?: string;
  /** Flag to indicate whether the field is disabled */
  disabled?: boolean;
  /** Defines the width of the Dropdown element */
  dropdownWidth?: number | string;
  /** Flag to indicate whether the field has an invalid value */
  isInvalid?: boolean;
  /**
   * Indicates if the Select is opened
   * Note: this prop is needed in used you want to open the select from the application.
   * It cannot be used to close the autocomplete.
   * */
  isOpen?: boolean;
  /** Indicates label of the Select */
  label?: string;
  /** Custom content to place in the left side */
  leftContent?: React.ReactNode;
  /** Indicates the number of items to be displayed in the dropdown when it is opened */
  maxVisibleOptions?: number;
  /** Name given to the component */
  name: string;
  /** Text to display in the header of the option list */
  optionsHeaderText?: string;
  /** Flag to indicate if the label should be visible */
  showLabel?: boolean;
  /** Flag to indicate whether the chevron icon is displayed */
  showRightIcon?: boolean;
  /** Flag to hide from outside the dropdown */
  showDropdown?: boolean;
  /** Indicates the position of the dropdown relative to the Input */
  placement?: PopoutPlacement;
  /** Indicates the value of the Select */
  value?: ISelectOption | null;
  /** Function to be executed when changing the selected value */
  onChange?: (name: string, value: ISelectOption | null) => void;
  /**
   * Function to activate clear indicator and define onClick callback.
   * Helper and onClear cannot be active at the same time
   * */
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Function to be executed when clicking outside the dropdown */
  onClickAway?: () => void;
  /** Custom class name for the field content */
  fieldContentClassName?: string;
}>;

/**
 * Defines a component which allows select options that are displayed inside of a dropdown by using a Textfield.
 */

export const Select = t0 => {
  const $ = _c(102);
  let children;
  let clearButtonTooltip;
  let dataId;
  let dropdownWidth;
  let fieldContentClassName;
  let isOpen;
  let label;
  let leftContent;
  let name;
  let onChange;
  let onClear;
  let onClickAway;
  let optionsHeaderText;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let value;
  if ($[0] !== t0) {
    ({
      clearButtonTooltip,
      children,
      dataId,
      disabled: t1,
      dropdownWidth,
      isOpen,
      leftContent,
      label,
      maxVisibleOptions: t2,
      name,
      optionsHeaderText,
      showRightIcon: t3,
      showDropdown: t4,
      placement: t5,
      showLabel: t6,
      value,
      onChange,
      onClear,
      onClickAway,
      fieldContentClassName,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = clearButtonTooltip;
    $[3] = dataId;
    $[4] = dropdownWidth;
    $[5] = fieldContentClassName;
    $[6] = isOpen;
    $[7] = label;
    $[8] = leftContent;
    $[9] = name;
    $[10] = onChange;
    $[11] = onClear;
    $[12] = onClickAway;
    $[13] = optionsHeaderText;
    $[14] = rest;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = value;
  } else {
    children = $[1];
    clearButtonTooltip = $[2];
    dataId = $[3];
    dropdownWidth = $[4];
    fieldContentClassName = $[5];
    isOpen = $[6];
    label = $[7];
    leftContent = $[8];
    name = $[9];
    onChange = $[10];
    onClear = $[11];
    onClickAway = $[12];
    optionsHeaderText = $[13];
    rest = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    value = $[21];
  }
  const disabled = t1 === undefined ? false : t1;
  const maxVisibleOptions = t2 === undefined ? 5 : t2;
  const showRightIcon = t3 === undefined ? true : t3;
  const showDropdown = t4 === undefined ? true : t4;
  const placement = t5 === undefined ? "bottom-start" : t5;
  const showLabel = t6 === undefined ? true : t6;
  const [inputElement, setInputElement] = React.useState(null);
  const {
    on: isDropdownOpen,
    toggle
  } = useToggle(false);
  let t7;
  let t8;
  if ($[22] !== isOpen || $[23] !== name) {
    t7 = () => {
      if (isOpen) {
        document.getElementById(name)?.click();
      }
    };
    t8 = [name, isOpen];
    $[22] = isOpen;
    $[23] = name;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  React.useEffect(t7, t8);
  let t9;
  if ($[26] !== name || $[27] !== onChange || $[28] !== toggle) {
    t9 = value_0 => {
      onChange?.(name, value_0 || null);
      toggle();
    };
    $[26] = name;
    $[27] = onChange;
    $[28] = toggle;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleOnChange = t9;
  let t10;
  if ($[30] !== isDropdownOpen || $[31] !== onClickAway || $[32] !== toggle) {
    t10 = () => {
      onClickAway?.();
      isDropdownOpen && toggle();
    };
    $[30] = isDropdownOpen;
    $[31] = onClickAway;
    $[32] = toggle;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const handleClickAway = t10;
  let t11;
  if ($[34] !== disabled || $[35] !== isDropdownOpen || $[36] !== toggle) {
    t11 = () => {
      if (disabled) {
        return;
      }
      !isDropdownOpen && toggle();
    };
    $[34] = disabled;
    $[35] = isDropdownOpen;
    $[36] = toggle;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const handleOnClick = t11;
  let t12;
  if ($[38] !== name || $[39] !== onChange || $[40] !== onClear) {
    t12 = event => {
      onChange?.(name, null);
      onClear?.(event);
    };
    $[38] = name;
    $[39] = onChange;
    $[40] = onClear;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const handleOnClear = t12;
  let t13;
  bb0: {
    if (!showRightIcon) {
      t13 = undefined;
      break bb0;
    }
    const iconName = isDropdownOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN;
    const t14 = `${name}-btn`;
    let t15;
    if ($[42] !== iconName) {
      t15 = <Icon name={iconName} />;
      $[42] = iconName;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== handleOnClick || $[45] !== name || $[46] !== t14 || $[47] !== t15) {
      t16 = <Button id={name} variant="text" className={getFieldChevronStylesAsClassName} onClick={handleOnClick} data-testid={t14}>{t15}</Button>;
      $[44] = handleOnClick;
      $[45] = name;
      $[46] = t14;
      $[47] = t15;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    t13 = t16;
  }
  const rightIcon = t13;
  let t14;
  if ($[49] !== leftContent) {
    t14 = leftContent && <FieldSideContent>{leftContent}</FieldSideContent>;
    $[49] = leftContent;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== disabled || $[52] !== isDropdownOpen || $[53] !== label || $[54] !== name || $[55] !== showLabel || $[56] !== value?.label) {
    t15 = showLabel && label && <FieldLabel aria-label={label} htmlFor={name} hasFocusedValue={isDropdownOpen || value?.label !== undefined} isDisabled={disabled} data-testid={`${name}-label`}>{label}</FieldLabel>;
    $[51] = disabled;
    $[52] = isDropdownOpen;
    $[53] = label;
    $[54] = name;
    $[55] = showLabel;
    $[56] = value?.label;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  const t16 = `${name}-select`;
  let t17;
  if ($[58] !== disabled || $[59] !== showLabel) {
    t17 = getTypographySelectStylesAsClassName(showLabel, disabled);
    $[58] = disabled;
    $[59] = showLabel;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  const t18 = value?.label || "";
  let t19;
  if ($[61] !== handleOnClick || $[62] !== t16 || $[63] !== t17 || $[64] !== t18) {
    t19 = <Typography id={t16} bold={true} className={t17} role="button" variant="subtitle2" onClick={handleOnClick}>{t18}</Typography>;
    $[61] = handleOnClick;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const t20 = value?.value || "";
  let t21;
  if ($[66] !== name || $[67] !== t20) {
    t21 = <input type="hidden" name={name} value={t20} />;
    $[66] = name;
    $[67] = t20;
    $[68] = t21;
  } else {
    t21 = $[68];
  }
  let t22;
  if ($[69] !== fieldContentClassName || $[70] !== t15 || $[71] !== t19 || $[72] !== t21) {
    t22 = <FieldContent className={fieldContentClassName}>{t15}{t19}{t21}</FieldContent>;
    $[69] = fieldContentClassName;
    $[70] = t15;
    $[71] = t19;
    $[72] = t21;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== clearButtonTooltip || $[75] !== disabled || $[76] !== handleOnClear || $[77] !== onClear || $[78] !== rightIcon || $[79] !== value) {
    t23 = (rightIcon || onClear) && <FieldSideContent align="right">{onClear && value && <ButtonIcon ariaDescribedby={clearButtonTooltip || ""} ariaLabel="Clear selection" icon={IconNames.CIRCLE_X} iconSolid={true} iconSize="sm" variant="text" onClick={handleOnClear} disabled={disabled}>{clearButtonTooltip}</ButtonIcon>}{rightIcon}</FieldSideContent>;
    $[74] = clearButtonTooltip;
    $[75] = disabled;
    $[76] = handleOnClear;
    $[77] = onClear;
    $[78] = rightIcon;
    $[79] = value;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  let t24;
  if ($[81] !== disabled || $[82] !== isDropdownOpen || $[83] !== rest || $[84] !== t14 || $[85] !== t22 || $[86] !== t23) {
    t24 = <FieldContainer isFocused={isDropdownOpen} disabled={disabled} ref={setInputElement} {...rest}>{t14}{t22}{t23}</FieldContainer>;
    $[81] = disabled;
    $[82] = isDropdownOpen;
    $[83] = rest;
    $[84] = t14;
    $[85] = t22;
    $[86] = t23;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  const t25 = showDropdown && isDropdownOpen && !disabled;
  let t26;
  if ($[88] !== children || $[89] !== dataId || $[90] !== dropdownWidth || $[91] !== handleClickAway || $[92] !== handleOnChange || $[93] !== inputElement || $[94] !== maxVisibleOptions || $[95] !== optionsHeaderText || $[96] !== placement || $[97] !== t25) {
    t26 = <DropdownOptionList isOpen={t25} reference={inputElement} onChange={handleOnChange} onClickAway={handleClickAway} dataId={dataId} placement={placement} optionsHeaderText={optionsHeaderText} maxVisibleOptions={maxVisibleOptions} dropdownWidth={dropdownWidth}>{children}</DropdownOptionList>;
    $[88] = children;
    $[89] = dataId;
    $[90] = dropdownWidth;
    $[91] = handleClickAway;
    $[92] = handleOnChange;
    $[93] = inputElement;
    $[94] = maxVisibleOptions;
    $[95] = optionsHeaderText;
    $[96] = placement;
    $[97] = t25;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  let t27;
  if ($[99] !== t24 || $[100] !== t26) {
    t27 = <>{t24}{t26}</>;
    $[99] = t24;
    $[100] = t26;
    $[101] = t27;
  } else {
    t27 = $[101];
  }
  return t27;
};